import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Package,
  Download,
  Lock,
  Unlock,
  Calendar,
  ShoppingBag,
  FileText,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { getCurrentUser } from '@/utils/config';
import { useToast } from '@/contexts/ToastContext';

const Dashboard = () => {
  const user = getCurrentUser();
  const toast = useToast();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPurchases: 0,
    totalSpent: 0,
    activeProducts: 0
  });

  useEffect(() => {
    if (user) {
      fetchPurchases();
    }
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Don't make the request if there's no token
      if (!token) {
        setPurchases([]);
        setLoading(false);
        return;
      }

      // Fetch user's premium access
      const response = await fetch('/.netlify/functions/premium-access', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 as expected (user not authenticated or token expired)
      if (response.status === 401) {
        setPurchases([]);
        setStats({
          totalPurchases: 0,
          totalSpent: 0,
          activeProducts: 0
        });
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.success && data.data) {
        const products = data.data.products || [];
        setPurchases(products);

        // Calculate stats
        const totalSpent = products.reduce((sum, item) => {
          // Assuming the product has a price somewhere in the structure
          return sum + parseFloat(item.product?.price || 0);
        }, 0);
        setStats({
          totalPurchases: products.length,
          totalSpent: totalSpent,
          activeProducts: products.filter(item => item.access?.can_download).length
        });
      } else {
        setPurchases([]);
        setStats({
          totalPurchases: 0,
          totalSpent: 0,
          activeProducts: 0
        });
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
      setStats({
        totalPurchases: 0,
        totalSpent: 0,
        activeProducts: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (productId, productName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/.netlify/functions/product-download?productId=${productId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success && data.data.downloadUrl) {
        // Open download URL in new tab
        window.open(data.data.downloadUrl, '_blank');

        // Refresh purchases to update download count
        setTimeout(() => fetchPurchases(), 1000);
      } else {
        toast.error(data.message || 'Failed to generate download link');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download product');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Please Login
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You need to be logged in to view your dashboard
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome back, {user.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your purchases and premium content
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Total Purchases
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.totalPurchases}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <ShoppingBag className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Total Spent
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  GH₵{stats.totalSpent.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                <Package className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Active Products
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.activeProducts}
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Unlock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link
            to="/shop"
            className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="flex items-center justify-between text-white">
              <div>
                <h3 className="text-xl font-bold mb-2">Browse Products</h3>
                <p className="text-emerald-100">Discover more premium content</p>
              </div>
              <ShoppingBag className="w-8 h-8 group-hover:scale-110 transition-transform" />
            </div>
          </Link>

          <Link
            to="/blog"
            className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="flex items-center justify-between text-white">
              <div>
                <h3 className="text-xl font-bold mb-2">Read Blog</h3>
                <p className="text-blue-100">Learn tips and tutorials</p>
              </div>
              <FileText className="w-8 h-8 group-hover:scale-110 transition-transform" />
            </div>
          </Link>
        </div>

        {/* My Purchases */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              My Premium Content
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Access your purchased products and downloads
            </p>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : purchases.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No purchases yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Browse our products to get started
                </p>
                <Link
                  to="/shop"
                  className="inline-block px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                >
                  Browse Products
                </Link>
              </div>
            ) : (
              <div className="grid gap-6">
                {purchases.map((purchase, index) => (
                  <motion.div
                    key={purchase.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-6 p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:shadow-md transition-all"
                  >
                    {/* Product Image */}
                    {purchase.image_url ? (
                      <img
                        src={purchase.image_url}
                        alt={purchase.product_name}
                        className="w-24 h-24 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-400" />
                      </div>
                    )}

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                            {purchase.product_name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Purchased on {formatDate(purchase.granted_at)}
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 text-sm rounded-full flex items-center gap-1">
                          <Unlock size={14} />
                          Unlocked
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                        <span className="flex items-center gap-1">
                          <Download size={14} />
                          {purchase.download_count || 0} / {purchase.max_downloads} downloads
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          Lifetime access
                        </span>
                      </div>

                      {/* Download Button */}
                      {purchase.downloads_remaining > 0 ? (
                        <button
                          onClick={() => handleDownload(purchase.product_id, purchase.product_name)}
                          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                        >
                          <Download size={18} />
                          Download Now ({purchase.downloads_remaining} remaining)
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-lg">
                          <AlertCircle size={18} />
                          Download limit reached
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Browse More */}
        {purchases.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Looking for more premium content?
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:opacity-90 transition-opacity"
            >
              <ShoppingBag size={20} />
              Browse All Products
              <ExternalLink size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
